import type { FieldType, FormField, FieldProperties } from '../types/form';

export interface FieldDefinition {
  type: FieldType;
  label: string;
  icon: string;
  category: 'basic' | 'advanced' | 'layout';
  defaultProperties: FieldProperties;
  defaultRequired: boolean;
}

export const fieldRegistry: Record<FieldType, FieldDefinition> = {
  text: {
    type: 'text',
    label: 'Text Input',
    icon: 'T',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  email: {
    type: 'email',
    label: 'Email',
    icon: '@',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  number: {
    type: 'number',
    label: 'Number',
    icon: '#',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  textarea: {
    type: 'textarea',
    label: 'Text Area',
    icon: '¶',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  select: {
    type: 'select',
    label: 'Dropdown',
    icon: '▼',
    category: 'basic',
    defaultProperties: { multiple: false },
    defaultRequired: false,
  },
  checkbox: {
    type: 'checkbox',
    label: 'Checkbox',
    icon: '☑',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  radio: {
    type: 'radio',
    label: 'Radio Group',
    icon: '◉',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  date: {
    type: 'date',
    label: 'Date',
    icon: '📅',
    category: 'advanced',
    defaultProperties: {},
    defaultRequired: false,
  },
  datetime: {
    type: 'datetime',
    label: 'Date & Time',
    icon: '🕐',
    category: 'advanced',
    defaultProperties: {},
    defaultRequired: false,
  },
  file: {
    type: 'file',
    label: 'File Upload',
    icon: '📎',
    category: 'advanced',
    defaultProperties: {
      maxSize: 10 * 1024 * 1024, // 10MB
      maxFiles: 1,
    },
    defaultRequired: false,
  },
  richtext: {
    type: 'richtext',
    label: 'Rich Text',
    icon: '📝',
    category: 'advanced',
    defaultProperties: {},
    defaultRequired: false,
  },
  signature: {
    type: 'signature',
    label: 'Signature',
    icon: '✍',
    category: 'advanced',
    defaultProperties: {},
    defaultRequired: false,
  },
  repeatable: {
    type: 'repeatable',
    label: 'Repeatable Section',
    icon: '🔄',
    category: 'layout',
    defaultProperties: {
      minItems: 1,
      maxItems: 10,
      addButtonText: 'Add Another',
      fields: [],
    },
    defaultRequired: false,
  },
  section: {
    type: 'section',
    label: 'Section',
    icon: '📋',
    category: 'layout',
    defaultProperties: {
      sectionTitle: 'Section Title',
      sectionDescription: '',
      collapsible: false,
      defaultCollapsed: false,
    },
    defaultRequired: false,
  },
  divider: {
    type: 'divider',
    label: 'Divider',
    icon: '—',
    category: 'layout',
    defaultProperties: {
      dividerStyle: 'solid',
      dividerColor: '#e5e7eb',
    },
    defaultRequired: false,
  },
  page_break: {
    type: 'page_break',
    label: 'Page Break',
    icon: '📄',
    category: 'layout',
    defaultProperties: {
      pageTitle: 'Page Title',
      pageDescription: '',
    },
    defaultRequired: false,
  },
  phone: {
    type: 'phone',
    label: 'Phone',
    icon: '📞',
    category: 'basic',
    defaultProperties: {
      countryCode: 'US',
      phoneFormat: 'international',
    },
    defaultRequired: false,
  },
  rating: {
    type: 'rating',
    label: 'Rating',
    icon: '⭐',
    category: 'advanced',
    defaultProperties: {
      ratingMax: 5,
      ratingIcon: 'star',
      allowHalf: false,
    },
    defaultRequired: false,
  },
  slider: {
    type: 'slider',
    label: 'Slider',
    icon: '🎚️',
    category: 'advanced',
    defaultProperties: {
      sliderMin: 0,
      sliderMax: 100,
      sliderStep: 1,
      showValue: true,
      showMinMax: true,
    },
    defaultRequired: false,
  },
  url: {
    type: 'url',
    label: 'URL',
    icon: '🔗',
    category: 'basic',
    defaultProperties: {},
    defaultRequired: false,
  },
  color: {
    type: 'color',
    label: 'Color Picker',
    icon: '🎨',
    category: 'advanced',
    defaultProperties: {
      colorFormat: 'hex',
      colorPresets: ['#3b82f6', '#ef4444', '#22c55e', '#f59e0b', '#8b5cf6', '#ec4899', '#06b6d4', '#000000', '#ffffff'],
    },
    defaultRequired: false,
  },
  hidden: {
    type: 'hidden',
    label: 'Hidden Field',
    icon: '👁️',
    category: 'advanced',
    defaultProperties: {
      hiddenValue: '',
    },
    defaultRequired: false,
  },
  matrix: {
    type: 'matrix',
    label: 'Matrix',
    icon: '📊',
    category: 'advanced',
    defaultProperties: {
      matrixRows: [
        { id: 'row1', label: 'Row 1' },
        { id: 'row2', label: 'Row 2' },
      ],
      matrixColumns: [
        { id: 'col1', label: 'Option 1', value: '1' },
        { id: 'col2', label: 'Option 2', value: '2' },
        { id: 'col3', label: 'Option 3', value: '3' },
      ],
      matrixType: 'single',
    },
    defaultRequired: false,
  },
  ranking: {
    type: 'ranking',
    label: 'Ranking',
    icon: '🏆',
    category: 'advanced',
    defaultProperties: {
      rankingItems: [
        { id: 'item1', label: 'Item 1' },
        { id: 'item2', label: 'Item 2' },
        { id: 'item3', label: 'Item 3' },
      ],
    },
    defaultRequired: false,
  },
  calculated: {
    type: 'calculated',
    label: 'Calculated',
    icon: '🔢',
    category: 'advanced',
    defaultProperties: {
      calculationType: 'sum',
      calculationFields: [],
      calculationFormat: 'number',
      calculationDecimals: 2,
    },
    defaultRequired: false,
  },
  address: {
    type: 'address',
    label: 'Address',
    icon: '📍',
    category: 'advanced',
    defaultProperties: {
      addressComponents: ['street', 'city', 'state', 'zip', 'country'],
      defaultCountry: 'US',
    },
    defaultRequired: false,
  },
  payment: {
    type: 'payment',
    label: 'Payment',
    icon: '💳',
    category: 'advanced',
    defaultProperties: {
      paymentProvider: 'stripe',
      paymentCurrency: 'USD',
      paymentMode: 'test',
    },
    defaultRequired: false,
  },
  appointment: {
    type: 'appointment',
    label: 'Appointment',
    icon: '📆',
    category: 'advanced',
    defaultProperties: {
      appointmentDuration: 30,
      appointmentMinAdvance: 24,
      appointmentMaxAdvance: 30,
      appointmentTimezone: 'America/New_York',
      appointmentAvailability: [
        { dayOfWeek: 1, slots: [{ start: '09:00', end: '17:00' }] },
        { dayOfWeek: 2, slots: [{ start: '09:00', end: '17:00' }] },
        { dayOfWeek: 3, slots: [{ start: '09:00', end: '17:00' }] },
        { dayOfWeek: 4, slots: [{ start: '09:00', end: '17:00' }] },
        { dayOfWeek: 5, slots: [{ start: '09:00', end: '17:00' }] },
      ],
      blockedDates: [],
    },
    defaultRequired: false,
  },
  nps: {
    type: 'nps',
    label: 'NPS Score',
    icon: '📈',
    category: 'advanced',
    defaultProperties: {
      npsLeftLabel: 'Not likely at all',
      npsRightLabel: 'Extremely likely',
      npsShowLabels: true,
    },
    defaultRequired: false,
  },
  country: {
    type: 'country',
    label: 'Country/State',
    icon: '🌍',
    category: 'advanced',
    defaultProperties: {
      countryFieldType: 'country_state',
      popularCountries: ['US', 'GB', 'CA', 'AU', 'DE', 'FR'],
    },
    defaultRequired: false,
  },
  image_choice: {
    type: 'image_choice',
    label: 'Image Choice',
    icon: '🖼️',
    category: 'advanced',
    defaultProperties: {
      imageChoices: [],
      imageChoiceColumns: 3,
      imageChoiceMultiple: false,
      imageChoiceShowLabels: true,
    },
    defaultRequired: false,
  },
  opinion_scale: {
    type: 'opinion_scale',
    label: 'Opinion Scale',
    icon: '📊',
    category: 'advanced',
    defaultProperties: {
      opinionScaleMin: 1,
      opinionScaleMax: 5,
      opinionScaleLeftLabel: 'Strongly Disagree',
      opinionScaleRightLabel: 'Strongly Agree',
      opinionScaleShowNumbers: true,
    },
    defaultRequired: false,
  },
};

export function createField(type: FieldType, name?: string): FormField {
  const definition = fieldRegistry[type];
  const id = crypto.randomUUID();
  const fieldName = name || `field_${id.substring(0, 8)}`;

  // For layout fields, use the type as the label
  const label = type === 'section'
    ? 'Section Title'
    : type === 'divider'
    ? ''
    : type === 'page_break'
    ? 'Page Title'
    : definition.label;

  return {
    id,
    type,
    label,
    name: fieldName,
    placeholder: '',
    helpText: '',
    required: definition.defaultRequired,
    validation: [],
    options: type === 'select' || type === 'radio' || type === 'checkbox'
      ? [
          { label: 'Option 1', value: 'option1' },
          { label: 'Option 2', value: 'option2' },
        ]
      : undefined,
    defaultValue: undefined,
    properties: { ...definition.defaultProperties },
  };
}

export function getFieldsByCategory(category: 'basic' | 'advanced' | 'layout'): FieldDefinition[] {
  return Object.values(fieldRegistry).filter((f) => f.category === category);
}
